/**
 * Every string the post list renders.
 *
 * The lifecycle note is the one long sentence on this page, and it earns its
 * place: it names the four states and who moves between them, which is the one
 * thing an operator cannot read off the table. Everything else the screen shows
 * is not restated in prose.
 */
export const copy = {
  crumbs: {
    root: 'لوحة التشغيل',
    current: 'المقالات',
  },

  head: {
    title: 'المقالات',
    lede: 'الطبقة التحريرية حول الفهرس: حوارات وقراءات وأدلّة مشاهدة، لكلٍّ منها موضوعٌ وكاتبٌ وتاريخ نشر وحالةٌ في دورة النشر نفسها التي تحكم العناوين.',
  },

  section: {
    heading: 'كلّ المقالات',
    add: 'أضف مقالاً',
    lifecycle:
      'أربع حالات: مسوّدة يراها كاتبها وحده، ثمّ بانتظار المراجعة حين يرفعها، ثمّ منشور أو مردود بيد مراجِع غير كاتبها. الكاتب لا ينشر مقاله بنفسه، والحذف يمحو المقال ولا يتركه حالةً في هذه القائمة.',
    caption: 'المقالات مرتّبةً بتاريخ النشر من الأحدث',
  },

  /** The facet is the review queue; «الكلّ» leads and the states follow. */
  facet: {
    label: 'الحال',
    groupLabel: 'ترشيح المقالات بالحال',
    all: 'الكلّ',
  },

  /** The list's own search — served by the route's `q`, folded like every Arabic search here. */
  search: {
    label: 'ابحث في المقالات بالعنوان',
    placeholder: 'ابحث بالعنوان…',
    hint: 'البحث يجري على كامل المقالات، لا على الصفحة المعروضة وحدها.',
  },

  emptyFilter: {
    message: 'لا مقال في هذه الحال الآن.',
    /** Names the term back, so the reader sees what was actually searched. */
    searched: (term: string) => `لا مقال يطابق «${term}».`,
    reset: 'اعرض كلّ المقالات',
  },

  columns: {
    post: 'المقال',
    state: 'الحالة',
    author: 'الكاتب',
    date: 'تاريخ النشر',
    menu: 'أفعال المقال',
  },

  /** Sorting is a view the reader chooses; it writes nothing on the post. */
  sort: {
    descending: 'رتّب بالأقدم أوّلاً',
    ascending: 'رتّب بالأحدث أوّلاً',
  },

  row: {
    menuLabel: (title: string) => `أفعال مقال «${title}»`,
    noTags: 'بلا وسوم بعد',
    /** A post with no publication date says so; a dash would be a claim. */
    notPublished: 'لم يُنشر بعد',
  },

  announce: {
    submitted: (title: string) => `رُفع «${title}» للمراجعة.`,
    published: (title: string) => `نُشر «${title}».`,
    rejected: (title: string) => `رُدّ «${title}» إلى كاتبه.`,
    withdrawn: (title: string) => `أُعيد «${title}» مسوّدةً.`,
    /**
     * The published path's own announcement. It names the public effect first,
     * because that is the part an operator needs to hear back — the state it
     * landed in is the badge's job.
     */
    unpublished: (title: string) => `أُلغي نشر «${title}» ورُفع من الموقع، وعاد مسوّدةً.`,
    deleted: (title: string) => `حُذف «${title}» نهائياً وخرج من القائمة.`,
  },

  /**
   * The traversal control. `backend-content` pages this list by offset and
   * returns the total it drew from, so the pager states the page of how many and
   * the rows of how many.
   *
   * The figure it reports is what the **facet** left standing on this page, not
   * what the page carried — the table beneath it shows the first, and two
   * numbers for one table is worse than one number that matches.
   */
  pager: {
    label: 'صفحات المقالات',
    shownOne: 'مقال واحد معروض',
    shownTwo: 'مقالان معروضان',
    shownFew: 'مقالات معروضة',
    shownMany: 'مقالاً معروضاً',
  },

  states: {
    loading: 'جارٍ تحميل المقالات',
    empty: 'لا مقالات بعد. ابدأ بأوّل مقال من «أضف مقالاً».',
  },
} as const;
